import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import { Button } from './Button'
import { BottomSheet } from './BottomSheet'

export interface CardKebabMenuItem {
  label: string
  onClick: () => void
  variant?: 'default' | 'error'
}

export interface CardKebabMenuProps {
  items: CardKebabMenuItem[]
  ariaLabel?: string
}

export function CardKebabMenu({ items, ariaLabel }: CardKebabMenuProps) {
  const { t: tc } = useTranslation('common')
  // Desktop: an anchored portal dropdown. Mobile (<sm): a bottom action sheet
  // (owner request 2026-07-27) — chosen at click time via innerWidth so we keep
  // ONE trigger (no duplicate aria-label) and stay test-safe (jsdom has no
  // matchMedia; its 1024px width keeps unit tests on the dropdown path).
  const [open, setOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (!open) return
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    update()
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  return (
    <div className="relative inline-block">
      <Button
        ref={triggerRef}
        type="button"
        variant="ghost"
        size="xs"
        aria-label={ariaLabel ?? tc('labels.actions_menu')}
        aria-haspopup="menu"
        aria-expanded={open || sheetOpen}
        onClick={(e) => {
          e.stopPropagation()
          if (typeof window !== 'undefined' && window.innerWidth < 640) {
            setSheetOpen(true)
          } else {
            setOpen((v) => !v)
          }
        }}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Button>

      {/* Desktop — anchored portal dropdown. */}
      {open && pos && createPortal(
        <ul
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
          className="menu bg-base-100 rounded-box shadow-lg z-50 w-36 p-1 border border-base-200"
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((item) => (
            <li key={item.label}>
              <button
                type="button"
                role="menuitem"
                className={item.variant === 'error' ? 'text-error' : ''}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(false)
                  item.onClick()
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}

      {/* Mobile — bottom action sheet. */}
      <BottomSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        ariaLabel={ariaLabel ?? tc('labels.actions_menu')}
      >
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <Button
              key={item.label}
              type="button"
              variant="ghost"
              block
              className={`justify-start ${item.variant === 'error' ? 'text-error' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                setSheetOpen(false)
                item.onClick()
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </BottomSheet>
    </div>
  )
}
